import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Pagination,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  activityLogService,
  ActivityLog,
  ActivityLogFilters,
} from '../services/activityLogService';
import { History, Visibility } from '@mui/icons-material';

const ActivityLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'service_price_changed'>('service_price_changed');
  
  // Filters
  const [filters, setFilters] = useState<ActivityLogFilters>({
    page: 1,
    limit: 20,
    activityType: 'service_price_changed',
  });
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [userTypeFilter, setUserTypeFilter] = useState<'doctor' | 'patient' | ''>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<'chat' | 'audioCall' | 'videoCall' | ''>('');
  const [changedByFilter, setChangedByFilter] = useState<'doctor' | 'admin' | ''>('');
  
  // Data
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });

  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filtersWithDates: ActivityLogFilters = {
        ...filters,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        userType: userTypeFilter || undefined,
        activityType: activeTab,
        userId: userIdFilter ? parseInt(userIdFilter) : undefined,
      };

      const response = await activityLogService.getActivityLogs(filtersWithDates);
      if (response.success) {
        // Apply client-side filtering for service-specific fields
        let filteredLogs = response.data.logs || [];
        
        if (activeTab === 'service_price_changed') {
          if (serviceTypeFilter) {
            filteredLogs = filteredLogs.filter(log => 
              log.activityJson?.service === serviceTypeFilter
            );
          }
          if (changedByFilter) {
            filteredLogs = filteredLogs.filter(log => 
              log.activityJson?.changedBy === changedByFilter
            );
          }
        }
        
        setLogs(filteredLogs);
        setPagination(prev => response.data.pagination || prev);
      } else {
        setError(response.message || 'Failed to load activity logs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [filters, startDate, endDate, userTypeFilter, activeTab, userIdFilter, serviceTypeFilter, changedByFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters({ ...filters, page: value });
  };

  const handleViewDetails = (log: ActivityLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'service_price_changed':
        return 'Service Price Changed';
      default:
        return type;
    }
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case 'chat':
        return 'Chat';
      case 'audioCall':
        return 'Audio Call';
      case 'videoCall':
        return 'Video Call';
      default:
        return service;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'service_price_changed') => {
    setActiveTab(newValue);
    setFilters({ ...filters, page: 1, activityType: newValue });
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <History sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Activity Logs
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 64,
            },
          }}
        >
          <Tab label="Service Price Changed" value="service_price_changed" />
        </Tabs>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {/* <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userTypeFilter}
                  label="User Type"
                  onChange={(e) => setUserTypeFilter(e.target.value as any)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="doctor">Doctor</MenuItem>
                  <MenuItem value="patient">Patient</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="User ID"
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                type="number"
              />
            </Grid> */}
            
            {/* Service Price Changed specific filters */}
            {activeTab === 'service_price_changed' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Service Type</InputLabel>
                    <Select
                      value={serviceTypeFilter}
                      label="Service Type"
                      onChange={(e) => setServiceTypeFilter(e.target.value as any)}
                    >
                      <MenuItem value="">All Services</MenuItem>
                      <MenuItem value="chat">Chat</MenuItem>
                      <MenuItem value="audioCall">Audio Call</MenuItem>
                      <MenuItem value="videoCall">Video Call</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Changed By</InputLabel>
                    <Select
                      value={changedByFilter}
                      label="Changed By"
                      onChange={(e) => setChangedByFilter(e.target.value as any)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Activity Logs List */}
      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No activity logs found
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {logs.map((log) => {
                const oldPrice = typeof log.activityJson?.oldPrice === 'string' 
                  ? parseFloat(log.activityJson.oldPrice) 
                  : (log.activityJson?.oldPrice || 0);
                const newPrice = typeof log.activityJson?.newPrice === 'string'
                  ? parseFloat(log.activityJson.newPrice)
                  : (log.activityJson?.newPrice || 0);
                const priceChange = newPrice - oldPrice;
                const priceChangePercent = oldPrice > 0
                  ? ((priceChange / oldPrice) * 100).toFixed(1)
                  : '0';

                return (
                  <Card key={log.id} sx={{ '&:hover': { boxShadow: 4 } }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {log.user?.fullName || `User ID: ${log.userId}`}
                            </Typography>
                            <Chip
                              label={log.userType}
                              color={log.userType === 'doctor' ? 'primary' : 'secondary'}
                              size="small"
                            />
                            <Chip
                              label={getActivityTypeLabel(log.activityType)}
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          
                          {log.user && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {log.user.email} • {log.user.phoneNumber}
                            </Typography>
                          )}

                          {activeTab === 'service_price_changed' && log.activityJson && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Service
                                  </Typography>
                                  <Chip
                                    label={getServiceLabel(log.activityJson.service || '')}
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 0.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Old Price
                                  </Typography>
                                  <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                                    ₹{oldPrice.toFixed(2)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    New Price
                                  </Typography>
                                  <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                                    ₹{newPrice.toFixed(2)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Price Change
                                  </Typography>
                                  <Chip
                                    label={`${priceChange >= 0 ? '+' : ''}₹${priceChange.toFixed(2)} (${priceChangePercent}%)`}
                                    color={priceChange > 0 ? 'error' : priceChange < 0 ? 'success' : 'default'}
                                    size="small"
                                    variant={priceChange === 0 ? 'outlined' : 'filled'}
                                    sx={{ mt: 0.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Old Status
                                  </Typography>
                                  <Chip
                                    label={log.activityJson.oldEnabled ? 'Enabled' : 'Disabled'}
                                    color={log.activityJson.oldEnabled ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 0.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    New Status
                                  </Typography>
                                  <Chip
                                    label={log.activityJson.newEnabled ? 'Enabled' : 'Disabled'}
                                    color={log.activityJson.newEnabled ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 0.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Changed By
                                  </Typography>
                                  <Chip
                                    label={log.activityJson.changedBy || 'Unknown'}
                                    color={log.activityJson.changedBy === 'admin' ? 'error' : 'info'}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Date & Time
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {formatDate(log.createdAt)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Box>
                        {/* <Box sx={{ ml: 2 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => handleViewDetails(log)}
                              color="primary"
                              size="small"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box> */}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </>
        )}
      </Box>

      {/* Details Dialog */}
      {showDetails && selectedLog && (
        <Card
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            zIndex: 1300,
            boxShadow: 6,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Activity Log Details</Typography>
              <IconButton onClick={() => setShowDetails(false)} size="small">
                ×
              </IconButton>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">ID</Typography>
              <Typography variant="body1">{selectedLog.id}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">User</Typography>
              {selectedLog.user ? (
                <Box>
                  <Typography variant="body1">{selectedLog.user.fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedLog.user.email} | {selectedLog.user.phoneNumber}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1">User ID: {selectedLog.userId}</Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">User Type</Typography>
              <Chip
                label={selectedLog.userType}
                color={selectedLog.userType === 'doctor' ? 'primary' : 'secondary'}
                size="small"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Activity Type</Typography>
              <Typography variant="body1">{getActivityTypeLabel(selectedLog.activityType)}</Typography>
            </Box>

            {selectedLog.activityJson && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Activity Details</Typography>
                <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(selectedLog.activityJson, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
              <Typography variant="body1">{formatDate(selectedLog.createdAt)}</Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ActivityLogs;

